/**
 * Thin fetch wrapper for the admin console. Ported from noteshq-web's
 * http.js but with two admin-specific differences:
 *
 *   1. The token lives under a separate localStorage key
 *      ("noteshq.admin.token") — completely disjoint from the
 *      end-user app's token. A logged-in admin can still also have
 *      a logged-in user session at app.noteshq.ai without either
 *      stealing the other's auth.
 *
 *   2. On 401, we don't simply clear the token — we also fire a
 *      window event so the AdminAuthContext can react and re-route
 *      to /login. The legacy admin.html used a hard
 *      window.location.href reassignment; here we keep navigation
 *      inside react-router so the URL bar stays clean and back-button
 *      behaviour is sensible.
 *
 * API_BASE is auto-detected: localhost in dev (Vite serves from
 * :5174, SpringBoot from :8080), api.noteshq.ai in prod. Override
 * via VITE_API_BASE if you need to point at staging.
 *
 * @author <lukman@zshift.io>
 */

const STORAGE_KEY = 'noteshq.admin.token';

export const API_BASE =
  import.meta.env.VITE_API_BASE ||
  (typeof window !== 'undefined' && window.location.origin.includes('localhost')
    ? 'http://localhost:8080'
    : 'https://api.noteshq.ai');

// ── Token storage ────────────────────────────────────────────────
// In-memory cache so multiple paint cycles don't all hit
// localStorage (cheap, but adds up at startup). Falls back to
// reading from storage on every cold boot.
let cachedToken = null;
let cachedHydrated = false;

export function getAdminToken() {
  if (!cachedHydrated) {
    try { cachedToken = localStorage.getItem(STORAGE_KEY); } catch { cachedToken = null; }
    cachedHydrated = true;
  }
  return cachedToken;
}

export function setAdminToken(token) {
  cachedToken = token || null;
  cachedHydrated = true;
  try {
    if (token) localStorage.setItem(STORAGE_KEY, token);
    else       localStorage.removeItem(STORAGE_KEY);
  } catch { /* private mode / disabled storage — fine, in-memory still works for this session */ }
}

export function clearAdminToken() { setAdminToken(null); }

// ── HTTP wrapper ─────────────────────────────────────────────────
/**
 * fetch() wrapper that:
 *   - Auto-injects Authorization: Bearer <jwt> when a token is cached
 *   - JSON-encodes the body when one is passed (no manual stringify)
 *   - Throws on non-2xx with the server's `message` / `error` field
 *   - On 401, clears the token + dispatches `noteshq.admin.unauth`
 *     so the AuthContext can react (UNLESS opts.silent401 is set —
 *     used for optional endpoints that may legitimately be missing
 *     on older deploys, so a 401/404 there doesn't punt the user
 *     back to the login screen)
 *   - Returns null for 204 No Content and other empty-body cases
 *
 * Usage:
 *   const users = await api('/api/admin/users?size=25');
 *   await api('/api/admin/users/42/ban', { method:'POST', body:{ reason } });
 *
 *   // For an endpoint that may not exist yet:
 *   const usage = await api(`/api/admin/users/${id}/usage`,
 *                           { silent401: true }).catch(() => null);
 */
export async function api(path, opts = {}) {
  const token = getAdminToken();
  // Multipart uploads pass FormData as the body. fetch() sets the
  // multipart boundary header for us, so we MUST NOT set Content-Type
  // ourselves — manually setting it strips the boundary token and
  // the server rejects the request as malformed.
  const isMultipart = opts.multipart === true;
  const headers = {
    ...(!isMultipart && opts.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    ...(token ? { 'Authorization': 'Bearer ' + token } : {}),
    ...(opts.headers || {}),
  };

  const url = path.startsWith('http') ? path : API_BASE + path;
  const res = await fetch(url, {
    method:  opts.method || 'GET',
    headers,
    body:    isMultipart
              ? opts.body
              : (opts.body !== undefined ? JSON.stringify(opts.body) : undefined),
    signal:  opts.signal,
  });

  if (res.status === 401) {
    if (opts.silent401) {
      // Caller is explicitly OK with a 401 here — don't touch the
      // global auth state. Treat it as "endpoint refused", same as
      // a 403 or 404 from the caller's perspective.
      throw new ApiError(401, 'Unauthorized (silent).');
    }
    clearAdminToken();
    // Tell the AuthContext to flip to "guest" state — it has a
    // window-event listener that catches this. Using a CustomEvent
    // means we don't have to thread a reference through every
    // module that imports api().
    try { window.dispatchEvent(new CustomEvent('noteshq.admin.unauth')); } catch { /* ssr */ }
    throw new ApiError(401, 'Session expired. Please sign in again.');
  }

  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const j = await res.json();
      msg = j.message || j.error || msg;
    } catch { /* not json */ }
    throw new ApiError(res.status, msg);
  }

  if (res.status === 204) return null;
  const ct = res.headers.get('content-type') || '';
  if (!ct.includes('application/json')) return null;
  return res.json();
}

export class ApiError extends Error {
  constructor(status, message) {
    super(message);
    this.status = status;
    this.name = 'ApiError';
  }
}
